import { useMemo, useState } from "react";
import { BookTemplate, ChevronDown, ChevronUp, Copy, FunctionSquare, List, Lock, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PythonCodeBlock } from "@/components/ui/PythonCodeEditor";
import { PresetFunctionModal } from "../components/PresetFunctionModal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { fetchTenderClients, type TenderClientOption } from "../api/tenders";
import {
  useCreatePricingPreset,
  useCreatePricingTemplate,
  useDeletePricingFunction,
  useDeletePricingPreset,
  useDeletePricingTemplate,
  usePricingFunctions,
  usePricingPresets,
  usePricingTemplates,
  useReviewPricingFunction,
  useUpdatePricingPreset,
  useUpdatePricingTemplate,
} from "../hooks/useTenders";
import type {
  PricingItemTemplate,
  PricingItemTemplateFormData,
  PricingPreset,
  PricingPresetEntryFormData,
  PricingPresetFormData,
  PricingPresetFunction,
  PricingPresetParameterFormData,
  PresetQuantityType,
} from "../types";
import { useQuery } from "@tanstack/react-query";
import {
  PRESET_FUNCTION_SORT_LABELS,
  filterPresetFunctions,
  presetFunctionCreators,
  type PresetFunctionSortKey,
} from "../presetFunctionFilters";

// ── Helpers ───────────────────────────────────────────────────────────────

function uid() {
  return crypto.randomUUID();
}

// ── Template form ─────────────────────────────────────────────────────────

interface TemplateFormState {
  name: string;
  base_rate: string;
  unit: string;
  notes: string;
  client: string;
  order: string;
  apply_multiplier: boolean;
}

function emptyTemplateForm(): TemplateFormState {
  return { name: "", base_rate: "", unit: "szt", notes: "", client: "", order: "0", apply_multiplier: true };
}

function templateToForm(t: PricingItemTemplate): TemplateFormState {
  return {
    name: t.name,
    base_rate: t.base_rate,
    unit: t.unit,
    notes: t.notes,
    client: t.client ?? "",
    order: String(t.order),
    apply_multiplier: t.apply_multiplier,
  };
}

// ── Preset form helpers ───────────────────────────────────────────────────

interface ParameterRow extends PricingPresetParameterFormData {
  _key: string;
}

interface EntryRow extends PricingPresetEntryFormData {
  _key: string;
}

function emptyParam(): ParameterRow {
  return { _key: uid(), key: "", label: "", unit: "", default_value: null, order: 0 };
}

function emptyEntry(): EntryRow {
  return {
    _key: uid(),
    template: null,
    custom_name: "",
    quantity_type: "manual",
    fixed_quantity: null,
    formula_parameter: "",
    formula_divisor: null,
    formula_offset: null,
    function: null,
    function_param_map: {},
    order: 0,
  };
}

// ── Tab component ─────────────────────────────────────────────────────────

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? "border-[var(--color-primary)] text-[var(--color-primary)] dark:text-blue-400 dark:border-[var(--color-primary)]"
          : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      }`}
    >
      {children}
    </button>
  );
}

// ── Template modal ────────────────────────────────────────────────────────

interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  editing: PricingItemTemplate | null;
  clients: TenderClientOption[];
}

function TemplateModal({ open, onClose, editing, clients }: TemplateModalProps) {
  const [form, setForm] = useState<TemplateFormState>(
    editing ? templateToForm(editing) : emptyTemplateForm()
  );
  const createMutation = useCreatePricingTemplate();
  const updateMutation = useUpdatePricingTemplate(editing?.id ?? "");

  const set = (field: keyof TemplateFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data: PricingItemTemplateFormData = {
      name: form.name.trim(),
      base_rate: form.base_rate,
      unit: form.unit.trim() || "szt",
      notes: form.notes,
      client: form.client || null,
      order: parseInt(form.order) || 0,
      apply_multiplier: form.apply_multiplier,
    };
    if (editing) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
    onClose();
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Edytuj pozycję katalogu" : "Nowa pozycja katalogu"}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nazwa pozycji <span className="text-red-500">*</span>
          </label>
          <input
            value={form.name}
            onChange={set("name")}
            required
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            placeholder="np. Kabel SN 12/20kV XRUHAKXS"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stawka bazowa netto (PLN) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.base_rate}
              onChange={set("base_rate")}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Jednostka <span className="text-red-500">*</span>
            </label>
            <input
              value={form.unit}
              onChange={set("unit")}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="szt / mb / kpl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Klient (opcjonalnie)
          </label>
          <select
            value={form.client}
            onChange={set("client")}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            <option value="">— Globalny (bez klienta) —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!form.apply_multiplier}
            onChange={(e) => setForm((prev) => ({ ...prev, apply_multiplier: !e.target.checked }))}
            className="mt-0.5 rounded border-gray-300"
          />
          <span>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nie podlega współczynnikowi mnożnikowemu
            </span>
            <span className="block text-xs text-gray-400 dark:text-gray-500">
              Pozycja będzie liczona 1:1 — współczynnik mnożnikowy wyceny jej nie zmieni.
            </span>
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Uwagi / komentarz
          </label>
          <textarea
            value={form.notes}
            onChange={set("notes")}
            rows={3}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
            placeholder="Dodatkowe informacje o pozycji..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Kolejność
          </label>
          <input
            type="number"
            min="0"
            value={form.order}
            onChange={set("order")}
            className="w-24 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            size="sm"
            loading={isPending}
            disabled={!form.name.trim() || !form.base_rate || !form.unit.trim()}
          >
            {editing ? "Zapisz zmiany" : "Dodaj pozycję"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Preset modal ──────────────────────────────────────────────────────────

interface PresetModalProps {
  open: boolean;
  onClose: () => void;
  editing: PricingPreset | null;
  /** When true, `editing` seeds the form but submitting creates a brand-new preset. */
  duplicating?: boolean;
  clients: TenderClientOption[];
  templates: PricingItemTemplate[];
}

function PresetModal({ open, onClose, editing, duplicating = false, clients, templates }: PresetModalProps) {
  const [name, setName] = useState(
    editing ? (duplicating ? `${editing.name} (kopia)` : editing.name) : ""
  );
  const [description, setDescription] = useState(editing?.description ?? "");
  const [clientId, setClientId] = useState(editing?.client ?? "");
  const [parameters, setParameters] = useState<ParameterRow[]>(
    editing?.parameters.map((p) => ({ ...p, _key: uid() })) ?? [emptyParam()]
  );
  const [entries, setEntries] = useState<EntryRow[]>(
    editing?.entries.map((e) => ({ ...e, _key: uid() })) ?? [emptyEntry()]
  );

  const createMutation = useCreatePricingPreset();
  const updateMutation = useUpdatePricingPreset(editing?.id ?? "");
  // Only approved (available) functions may be selected in preset entries.
  const { data: functions = [] } = usePricingFunctions({ status: "approved" });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const params: PricingPresetParameterFormData[] = parameters
      .filter((p) => p.key.trim() && p.label.trim())
      .map(({ _key, ...p }, idx) => ({ ...p, order: idx }));

    const ents: PricingPresetEntryFormData[] = entries
      .filter((en) => en.template || en.custom_name?.trim())
      .map(({ _key, ...en }, idx) => ({
        ...en,
        order: idx,
        template: en.template || null,
        custom_name: en.custom_name ?? "",
        fixed_quantity: en.fixed_quantity || null,
        formula_divisor: en.formula_divisor || null,
        formula_offset: en.formula_offset || null,
        function: en.quantity_type === "function" ? en.function || null : null,
        function_param_map: en.quantity_type === "function" ? en.function_param_map ?? {} : {},
      }));

    const data: PricingPresetFormData = {
      name: name.trim(),
      description,
      client: clientId || null,
      parameters: params,
      entries: ents,
    };

    if (editing && !duplicating) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
    onClose();
  };

  const updateParam = (key: string, field: keyof ParameterRow, value: string) => {
    setParameters((prev) =>
      prev.map((p) => (p._key === key ? { ...p, [field]: value } : p))
    );
  };

  const removeParam = (key: string) => {
    setParameters((prev) => (prev.length > 1 ? prev.filter((p) => p._key !== key) : prev));
  };

  const updateEntry = (key: string, field: keyof EntryRow, value: string | null) => {
    setEntries((prev) =>
      prev.map((e) => (e._key === key ? { ...e, [field]: value } : e))
    );
  };

  // Set which input parameter feeds a given function argument.
  const updateEntryParamMap = (key: string, argName: string, presetKey: string) => {
    setEntries((prev) =>
      prev.map((e) =>
        e._key === key
          ? { ...e, function_param_map: { ...(e.function_param_map ?? {}), [argName]: presetKey } }
          : e
      )
    );
  };

  const removeEntry = (key: string) => {
    setEntries((prev) => (prev.length > 1 ? prev.filter((e) => e._key !== key) : prev));
  };

  const moveEntry = (key: string, dir: -1 | 1) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e._key === key);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const moveParam = (key: string, dir: -1 | 1) => {
    setParameters((prev) => {
      const idx = prev.findIndex((p) => p._key === key);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  };

  const paramKeys = parameters.filter((p) => p.key.trim()).map((p) => p.key.trim());

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        editing
          ? duplicating
            ? "Nowy preset wyceny (na podstawie istniejącego)"
            : "Edytuj preset wyceny"
          : "Nowy preset wyceny"
      }
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nazwa presetu <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="np. Kabel SN z mufami"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Klient (opcjonalnie)
            </label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">— Globalny —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Opis
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] resize-none"
              placeholder="Krótki opis zastosowania presetu..."
            />
          </div>
        </div>

        {/* Parameters */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Parametry wejściowe
            </h3>
            <button
              type="button"
              onClick={() => setParameters((prev) => [...prev, emptyParam()])}
              className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] flex items-center gap-1"
            >
              <Plus size={12} /> Dodaj parametr
            </button>
          </div>
          <div className="space-y-2">
            {parameters.map((p, idx) => (
              <div key={p._key} className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex flex-col gap-0.5 mt-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => moveParam(p._key, -1)}
                    disabled={idx === 0}
                    className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20"
                  ><ChevronUp size={12} /></button>
                  <button
                    type="button"
                    onClick={() => moveParam(p._key, 1)}
                    disabled={idx === parameters.length - 1}
                    className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20"
                  ><ChevronDown size={12} /></button>
                </div>
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
                  <input
                    value={p.key}
                    onChange={(e) => updateParam(p._key, "key", e.target.value.replace(/\s/g, "_"))}
                    placeholder="klucz (np. dlugosc_m)"
                    className="w-full sm:w-32 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                  <input
                    value={p.label}
                    onChange={(e) => updateParam(p._key, "label", e.target.value)}
                    placeholder="Etykieta dla użytkownika"
                    className="w-full sm:flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                  <div className="flex gap-2">
                    <input
                      value={p.unit ?? ""}
                      onChange={(e) => updateParam(p._key, "unit", e.target.value)}
                      placeholder="jed."
                      className="w-20 sm:w-16 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    />
                    <input
                      type="number"
                      step="any"
                      value={p.default_value ?? ""}
                      onChange={(e) => updateParam(p._key, "default_value", e.target.value)}
                      placeholder="domyślna"
                      className="flex-1 sm:w-24 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeParam(p._key)}
                  disabled={parameters.length === 1}
                  className="p-1 mt-0.5 flex-shrink-0 text-gray-300 hover:text-red-500 disabled:opacity-20"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Entries */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Pozycje presetu
            </h3>
            <button
              type="button"
              onClick={() => setEntries((prev) => [...prev, emptyEntry()])}
              className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] flex items-center gap-1"
            >
              <Plus size={12} /> Dodaj pozycję
            </button>
          </div>
          <div className="space-y-3">
            {entries.map((entry, idx) => (
              <div key={entry._key} className="rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-0.5 mt-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveEntry(entry._key, -1)}
                      disabled={idx === 0}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20"
                    ><ChevronUp size={12} /></button>
                    <button
                      type="button"
                      onClick={() => moveEntry(entry._key, 1)}
                      disabled={idx === entries.length - 1}
                      className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-20"
                    ><ChevronDown size={12} /></button>
                  </div>

                  {/* Template or custom name */}
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
                    <select
                      value={entry.template ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateEntry(entry._key, "template", val || null);
                        if (val) updateEntry(entry._key, "custom_name", "");
                      }}
                      className="w-full sm:flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    >
                      <option value="">— Własna nazwa —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.base_rate} PLN/{t.unit})
                        </option>
                      ))}
                    </select>
                    {!entry.template && (
                      <input
                        value={entry.custom_name ?? ""}
                        onChange={(e) => updateEntry(entry._key, "custom_name", e.target.value)}
                        placeholder="Własna nazwa pozycji"
                        className="w-full sm:flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => removeEntry(entry._key)}
                    disabled={entries.length === 1}
                    className="p-1 mt-0.5 flex-shrink-0 text-gray-300 hover:text-red-500 disabled:opacity-20"
                  >
                    <X size={13} />
                  </button>
                </div>

                {/* Quantity type */}
                <div className="flex items-center gap-3 pl-6">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Ilość:</span>
                  {(["fixed", "formula", "function", "manual"] as PresetQuantityType[]).map((qt) => (
                    <label key={qt} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name={`qt-${entry._key}`}
                        value={qt}
                        checked={entry.quantity_type === qt}
                        onChange={() => updateEntry(entry._key, "quantity_type", qt)}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {qt === "fixed" ? "Stała" : qt === "formula" ? "Wzór" : qt === "function" ? "Funkcja" : "Ręcznie"}
                      </span>
                    </label>
                  ))}
                </div>

                {/* Fixed quantity */}
                {entry.quantity_type === "fixed" && (
                  <div className="pl-6">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={entry.fixed_quantity ?? ""}
                      onChange={(e) => updateEntry(entry._key, "fixed_quantity", e.target.value)}
                      placeholder="Ilość"
                      className="w-32 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    />
                  </div>
                )}

                {/* Formula */}
                {entry.quantity_type === "formula" && (
                  <div className="pl-6 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={entry.formula_parameter ?? ""}
                        onChange={(e) => updateEntry(entry._key, "formula_parameter", e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      >
                        <option value="">— wybierz parametr —</option>
                        {paramKeys.map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-400">offset:</span>
                      <input
                        type="number"
                        step="any"
                        value={entry.formula_offset ?? ""}
                        onChange={(e) => updateEntry(entry._key, "formula_offset", e.target.value)}
                        placeholder="0"
                        className="w-20 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      />
                      <span className="text-xs text-gray-400">dzielnik:</span>
                      <input
                        type="number"
                        step="any"
                        value={entry.formula_divisor ?? ""}
                        onChange={(e) => updateEntry(entry._key, "formula_divisor", e.target.value)}
                        placeholder="1"
                        className="w-20 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                      />
                    </div>
                    {entry.formula_parameter && (
                      <p className="text-xs font-mono text-[var(--color-primary)] dark:text-blue-400">
                        {(() => {
                          const offset = entry.formula_offset ? parseFloat(entry.formula_offset) : null;
                          const offsetStr = offset != null && offset !== 0
                            ? offset < 0
                              ? ` + ${Math.abs(offset)}`
                              : ` - ${offset}`
                            : "";
                          return `max(0, ⌊(${entry.formula_parameter}${offsetStr}) / ${entry.formula_divisor || "1"}⌋)`;
                        })()}
                      </p>
                    )}
                  </div>
                )}

                {/* Function: pick a function, then map each of its arguments to an input
                    parameter → max(0, funkcja(arg1=…, arg2=…)). */}
                {entry.quantity_type === "function" && (() => {
                  const fn = functions.find((f) => f.id === entry.function);
                  const pmap = entry.function_param_map ?? {};
                  return (
                    <div className="pl-6 space-y-1.5">
                      <div className="max-w-[240px]">
                        <SearchableSelect
                          value={entry.function ?? ""}
                          placeholder="— wybierz funkcję —"
                          onChange={(e) => updateEntry(entry._key, "function", e.target.value || null)}
                          options={functions.map((f) => ({
                            value: f.id,
                            label: `${f.name}${f.created_by_name ? ` — ${f.created_by_name}` : ""}`,
                          }))}
                          className="text-xs py-1 px-2"
                        />
                      </div>

                      {functions.length === 0 && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                          Brak dostępnych funkcji — dodaj i zatwierdź je w zakładce „Funkcje pozycji presetu".
                        </p>
                      )}

                      {/* One input-parameter select per function argument. */}
                      {fn && fn.param_names.map((argName) => (
                        <div key={argName} className="flex items-center gap-2">
                          <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 w-28 truncate">
                            {argName}
                          </span>
                          <span className="text-gray-400 text-xs">←</span>
                          <select
                            value={pmap[argName] ?? ""}
                            onChange={(e) => updateEntryParamMap(entry._key, argName, e.target.value)}
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                          >
                            <option value="">— wybierz parametr —</option>
                            {paramKeys.map((k) => (
                              <option key={k} value={k}>{k}</option>
                            ))}
                          </select>
                        </div>
                      ))}

                      {fn && fn.param_names.length > 0 && (
                        <p className="text-xs font-mono text-[var(--color-primary)] dark:text-blue-400">
                          {`max(0, funkcja_presetu(${fn.param_names
                            .map((n) => `${n}=${pmap[n] || "?"}`)
                            .join(", ")}))`}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {entry.quantity_type === "manual" && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 pl-6 italic">
                    Ilość zostanie wpisana ręcznie podczas tworzenia wyceny.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            size="sm"
            loading={isPending}
            disabled={!name.trim()}
          >
            {editing ? "Zapisz zmiany" : "Dodaj preset"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Tab 1: Templates ──────────────────────────────────────────────────────

function TemplatesTab() {
  const [clientFilter, setClientFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PricingItemTemplate | null>(null);

  const { data: templates = [], isLoading } = usePricingTemplates(
    showInactive ? { active: "false" } : undefined
  );

  const { data: clients = [] } = useQuery({
    queryKey: ["tenders-clients"],
    queryFn: fetchTenderClients,
  });

  const deleteMutation = useDeletePricingTemplate();

  const filtered = clientFilter
    ? templates.filter((t) => t.client === clientFilter || !t.client)
    : templates;

  const openCreate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const openEdit = (t: PricingItemTemplate) => {
    setEditingTemplate(t);
    setModalOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="w-full sm:w-auto border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        >
          <option value="">Wszyscy klienci</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300"
          />
          Pokaż nieaktywne
        </label>
        <div className="sm:ml-auto">
          <Button size="sm" onClick={openCreate} className="w-full sm:w-auto">
            <Plus size={13} /> Dodaj pozycję
          </Button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">
          Brak pozycji katalogowych.
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2">
            {filtered.map((t) => (
              <div
                key={t.id}
                className={`bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 ${!t.is_active ? "opacity-50" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100 leading-tight">{t.name}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                        {Number(t.base_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })}
                        <span className="ml-1 text-xs font-medium text-[var(--color-primary)] dark:text-blue-400">PLN</span>
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium">
                        {t.unit}
                      </span>
                      {!t.apply_multiplier && (
                        <span
                          title="Nie podlega współczynnikowi mnożnikowemu — liczona 1:1"
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded text-xs font-medium"
                        >
                          <Lock size={10} /> Bez współczynnika
                        </span>
                      )}
                      {t.is_active ? (
                        <Badge color="green">Aktywna</Badge>
                      ) : (
                        <Badge color="gray">Nieaktywna</Badge>
                      )}
                    </div>
                    {t.client_name && (
                      <span className="mt-1 inline-block px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-[var(--color-primary)] dark:text-blue-300 rounded-md text-xs font-medium">
                        {t.client_name}
                      </span>
                    )}
                    {t.notes && (
                      <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 truncate">{t.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(t)}
                      className="p-2 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
                      title="Edytuj"
                    >
                      <Pencil size={14} />
                    </button>
                    {t.is_active && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Dezaktywować pozycję "${t.name}"?`)) {
                            deleteMutation.mutate(t.id);
                          }
                        }}
                        className="p-2 rounded text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-gray-600 transition-colors"
                        title="Dezaktywuj"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block rounded-xl overflow-x-auto shadow-sm">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-100 dark:bg-gray-800">
                <tr className="text-xs text-gray-600 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</th>
                  <th className="text-right py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka bazowa</th>
                  <th className="text-center py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jednostka</th>
                  <th className="text-left py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Klient</th>
                  <th className="text-left py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Uwagi</th>
                  <th className="text-center py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="py-2 px-4 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((t, idx) => (
                  <tr
                    key={t.id}
                    className={`transition-colors hover:bg-blue-50/40 dark:hover:bg-gray-700/40 ${
                      idx % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50/60 dark:bg-gray-800/40"
                    } ${!t.is_active ? "opacity-50" : ""}`}
                  >
                    <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                      {t.name}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {Number(t.base_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })}
                      </span>
                      <span className="ml-1 text-xs font-medium text-[var(--color-primary)] dark:text-blue-400">PLN</span>
                      {!t.apply_multiplier && (
                        <span
                          title="Nie podlega współczynnikowi mnożnikowemu — liczona 1:1"
                          className="ml-1.5 inline-flex align-middle text-amber-500 cursor-help"
                        >
                          <Lock size={12} />
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium">
                        {t.unit}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs">
                      {t.client_name ? (
                        <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-[var(--color-primary)] dark:text-blue-300 rounded-md font-medium">
                          {t.client_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">Globalny</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-gray-400 text-xs max-w-[200px] truncate">
                      {t.notes || <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {t.is_active ? (
                        <Badge color="green">Aktywna</Badge>
                      ) : (
                        <Badge color="gray">Nieaktywna</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openEdit(t)}
                          className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary)] hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
                          title="Edytuj"
                        >
                          <Pencil size={13} />
                        </button>
                        {t.is_active && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Dezaktywować pozycję "${t.name}"?`)) {
                                deleteMutation.mutate(t.id);
                              }
                            }}
                            className="p-1.5 rounded text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-gray-600 transition-colors"
                            title="Dezaktywuj"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalOpen && (
        <TemplateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          editing={editingTemplate}
          clients={clients}
        />
      )}
    </div>
  );
}

// ── Tab 2: Presets ────────────────────────────────────────────────────────

function PresetsTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<PricingPreset | null>(null);
  const [duplicating, setDuplicating] = useState(false);

  const { data: presets = [], isLoading } = usePricingPresets();
  const { data: clients = [] } = useQuery({
    queryKey: ["tenders-clients"],
    queryFn: fetchTenderClients,
  });
  const { data: templates = [] } = usePricingTemplates();
  const deleteMutation = useDeletePricingPreset();

  const openCreate = () => {
    setEditingPreset(null);
    setDuplicating(false);
    setModalOpen(true);
  };

  const openEdit = (p: PricingPreset) => {
    setEditingPreset(p);
    setDuplicating(false);
    setModalOpen(true);
  };

  const openDuplicate = (p: PricingPreset) => {
    setEditingPreset(p);
    setDuplicating(true);
    setModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}>
          <Plus size={13} /> Dodaj preset
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : presets.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">
          Brak presetów wyceny. Dodaj pierwszy preset, aby automatycznie generować pozycje wyceny.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="bg-white dark:bg-gray-800 p-4 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{preset.name}</h3>
                  {preset.client_name && (
                    <span className="text-xs text-[var(--color-primary)] dark:text-blue-400">{preset.client_name}</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(preset)}
                    className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Edytuj"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => openDuplicate(preset)}
                    className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Utwórz nowy na podstawie tego"
                  >
                    <Copy size={13} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Dezaktywować preset "${preset.name}"?`)) {
                        deleteMutation.mutate(preset.id);
                      }
                    }}
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Usuń"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {preset.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
                  {preset.description}
                </p>
              )}

              <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <List size={11} />
                  {preset.parameters.length} parametr{preset.parameters.length === 1 ? "" : preset.parameters.length < 5 ? "y" : "ów"}
                </span>
                <span className="flex items-center gap-1">
                  <BookTemplate size={11} />
                  {preset.entries.length} pozycj{preset.entries.length === 1 ? "a" : preset.entries.length < 5 ? "e" : "i"}
                </span>
              </div>

              {preset.parameters.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {preset.parameters.map((p) => (
                    <span
                      key={p.id}
                      className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded font-mono"
                    >
                      {p.key}
                      {p.unit ? ` (${p.unit})` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <PresetModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          editing={editingPreset}
          duplicating={duplicating}
          clients={clients}
          templates={templates}
        />
      )}
    </div>
  );
}

// ── Tab 3: Functions ──────────────────────────────────────────────────────

const STATUS_BADGE: Record<PricingPresetFunction["status"], { label: string; cls: string }> = {
  pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved: { label: "Dostępna", cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  rejected: { label: "Odrzucona", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
};

const FN_SELECT_CLS =
  "border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

function FunctionsTab() {
  const [search, setSearch] = useState("");
  // Ten sam zestaw filtrów co na stronie deweloperskiej — to tutaj siedzi osoba,
  // która funkcje zatwierdza, więc „pokaż tylko oczekujące" przydaje się bardziej.
  const [statusFilter, setStatusFilter] = useState<"" | PricingPresetFunction["status"]>("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [sortKey, setSortKey] = useState<PresetFunctionSortKey>("status");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PricingPresetFunction | null>(null);

  const user = useAuthStore((s) => s.user);
  const canReview = !!(user?.is_ceo || user?.is_developer);

  const { data: functions = [], isLoading } = usePricingFunctions();
  const deleteMutation = useDeletePricingFunction();
  const reviewMutation = useReviewPricingFunction();

  const creators = useMemo(() => presetFunctionCreators(functions), [functions]);
  const pendingCount = functions.filter((fn) => fn.status === "pending").length;
  const filtered = useMemo(
    () => filterPresetFunctions(functions, {
      status: statusFilter, creator: creatorFilter, search, sortKey,
    }),
    [functions, statusFilter, creatorFilter, search, sortKey],
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (fn: PricingPresetFunction) => {
    setEditing(fn);
    setModalOpen(true);
  };
  const review = (id: string, action: "approve" | "reject" | "revoke") =>
    reviewMutation.mutate({ id, action });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtruj po nazwie…"
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "" | PricingPresetFunction["status"])}
          className={FN_SELECT_CLS}
        >
          <option value="">
            Wszystkie statusy{pendingCount > 0 ? ` (${pendingCount} oczekuje)` : ""}
          </option>
          <option value="pending">Oczekujące{pendingCount > 0 ? ` (${pendingCount})` : ""}</option>
          <option value="approved">Dostępne</option>
          <option value="rejected">Odrzucone</option>
        </select>
        <select
          value={creatorFilter}
          onChange={(e) => setCreatorFilter(e.target.value)}
          className={FN_SELECT_CLS}
        >
          <option value="">Wszyscy autorzy</option>
          {creators.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as PresetFunctionSortKey)}
          className={FN_SELECT_CLS}
        >
          {PRESET_FUNCTION_SORT_LABELS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <Button size="sm" className="ml-auto" onClick={openCreate}>
          <Plus size={13} /> Dodaj funkcję
        </Button>
      </div>

      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        Nowa funkcja wymaga zatwierdzenia przez CEO lub developera, zanim będzie dostępna w pozycjach presetu.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">
          {functions.length === 0
            ? "Brak funkcji. Dodaj funkcję, aby używać jej w pozycjach presetu."
            : "Brak funkcji pasujących do filtra."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((fn) => {
            const badge = STATUS_BADGE[fn.status];
            return (
            <div key={fn.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{fn.name}</h3>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Utworzył: {fn.created_by_name ?? "—"}
                    {fn.reviewed_by_name && fn.status !== "pending"
                      ? ` · ${fn.status === "approved" ? "zatwierdził" : "odrzucił"}: ${fn.reviewed_by_name}`
                      : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {/* CEO / developer review actions */}
                  {canReview && fn.status !== "approved" && (
                    <button
                      onClick={() => review(fn.id, "approve")}
                      disabled={reviewMutation.isPending}
                      className="px-2 py-1 rounded text-[11px] font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
                      title="Oznacz jako dostępną"
                    >
                      Zatwierdź
                    </button>
                  )}
                  {canReview && fn.status === "pending" && (
                    <button
                      onClick={() => review(fn.id, "reject")}
                      disabled={reviewMutation.isPending}
                      className="px-2 py-1 rounded text-[11px] font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                      title="Odrzuć funkcję"
                    >
                      Odrzuć
                    </button>
                  )}
                  {canReview && fn.status === "approved" && (
                    <button
                      onClick={() => review(fn.id, "revoke")}
                      disabled={reviewMutation.isPending}
                      className="px-2 py-1 rounded text-[11px] font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                      title="Wycofaj dostępność (wraca do oczekujących)"
                    >
                      Wycofaj
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(fn)}
                    className="p-1.5 rounded text-gray-400 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Edytuj"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Dezaktywować funkcję "${fn.name}"?`)) {
                        deleteMutation.mutate(fn.id);
                      }
                    }}
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-600"
                    title="Usuń"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              <PythonCodeBlock code={fn.code} className="mt-2" />
            </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <PresetFunctionModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function PricingCatalogPage() {
  const [activeTab, setActiveTab] = useState<"templates" | "presets" | "functions">("templates");

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Katalog wycen</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Zarządzaj pozycjami katalogowymi i presetami wyceny
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex gap-0">
          <Tab active={activeTab === "templates"} onClick={() => setActiveTab("templates")}>
            <span className="flex items-center gap-2">
              <List size={14} />
              Pozycje katalogowe
            </span>
          </Tab>
          <Tab active={activeTab === "presets"} onClick={() => setActiveTab("presets")}>
            <span className="flex items-center gap-2">
              <BookTemplate size={14} />
              Presety
            </span>
          </Tab>
          <Tab active={activeTab === "functions"} onClick={() => setActiveTab("functions")}>
            <span className="flex items-center gap-2">
              <FunctionSquare size={14} />
              Funkcje pozycji presetu
            </span>
          </Tab>
        </div>
      </div>

      {/* Content */}
      {activeTab === "templates" ? (
        <TemplatesTab />
      ) : activeTab === "presets" ? (
        <PresetsTab />
      ) : (
        <FunctionsTab />
      )}
    </div>
  );
}
